import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, NavLink } from "react-router-dom";
import { useState, useEffect, lazy, Suspense } from "react";
import { I18nProvider, useI18n } from "@/i18n";
import { AuthProvider, useAuth } from "@/auth";
import { Toaster } from "sonner";
import { Menu, LogOut, AlarmClock, X, RotateCw } from "lucide-react";
import { photoUrl } from "@/api";
import "@/App.css";

import AuthCallback from "@/pages/AuthCallback";

// Code splitting: ogni pagina è un chunk separato → primo caricamento molto più leggero
const Landing = lazy(() => import("@/pages/Landing"));
const Auth = lazy(() => import("@/pages/Auth"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const ChiSiamo = lazy(() => import("@/pages/ChiSiamo"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Cassa = lazy(() => import("@/pages/Cassa"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const Inventario = lazy(() => import("@/pages/Inventario"));
const ProductionHistory = lazy(() => import("@/pages/ProductionHistory"));
const DevAI = lazy(() => import("@/pages/DevAI"));
const Recipes = lazy(() => import("@/pages/Recipes"));
const Formula = lazy(() => import("@/pages/Formula"));
const Invoices = lazy(() => import("@/pages/Invoices"));
const Orders = lazy(() => import("@/pages/Orders"));
const Messages = lazy(() => import("@/pages/Messages"));
const Production = lazy(() => import("@/pages/Production"));
const Partners = lazy(() => import("@/pages/Partners"));
const Reports = lazy(() => import("@/pages/Reports"));
const Waste = lazy(() => import("@/pages/Waste"));
const Reorder = lazy(() => import("@/pages/Reorder"));
const Billing = lazy(() => import("@/pages/Billing"));
const Prenotazioni = lazy(() => import("@/pages/Prenotazioni"));
const PaymentResult = lazy(() => import("@/pages/PaymentResult"));
const Portal = lazy(() => import("@/pages/Portal"));
const Direzione = lazy(() => import("@/pages/Direzione"));
const Preventivi = lazy(() => import("@/pages/Preventivi"));
const B2B = lazy(() => import("@/pages/B2B"));
const Admin = lazy(() => import("@/pages/Admin"));
const Staff = lazy(() => import("@/pages/Staff"));
const Shifts = lazy(() => import("@/pages/Shifts"));
const Attendance = lazy(() => import("@/pages/Attendance"));
const Lab = lazy(() => import("@/pages/Lab"));
const Stats = lazy(() => import("@/pages/Stats"));
const Guide = lazy(() => import("@/pages/Guide"));
const TrashPage = lazy(() => import("@/pages/Trash"));
const IngredientsList = lazy(() => import("@/pages/IngredientsList"));
const Catalog = lazy(() => import("@/pages/Catalog"));
const Listino = lazy(() => import("@/pages/Listino"));
const DeptShop = lazy(() => import("@/pages/DeptShop"));
const DeptOverview = lazy(() => import("@/pages/DeptOverview"));
const Documents = lazy(() => import("@/pages/Documents"));
const PastDocs = lazy(() => import("@/pages/PastDocs"));
const LabelsHistory = lazy(() => import("@/pages/LabelsHistory"));
const Payments = lazy(() => import("@/pages/Payments"));
const Azienda = lazy(() => import("@/pages/Azienda"));
const FoodCost = lazy(() => import("@/pages/FoodCost"));
const SpecialDays = lazy(() => import("@/pages/SpecialDays"));
const PurchasedProducts = lazy(() => import("@/pages/PurchasedProducts"));
const Developer = lazy(() => import("@/pages/Developer"));
const SignDelivery = lazy(() => import("@/pages/SignDelivery"));
const Caldane = lazy(() => import("@/pages/Caldane"));
const Ricorrenti = lazy(() => import("@/pages/Ricorrenti"));
const Torte = lazy(() => import("@/pages/Torte"));
const Avanzi = lazy(() => import("@/pages/Avanzi"));
const ClockPage = lazy(() => import("@/pages/Clock"));
const MyShifts = lazy(() => import("@/pages/MyShifts"));
const PublicDoc = lazy(() => import("@/pages/PublicDoc"));
const TraceView = lazy(() => import("@/pages/TraceView"));
const Sala = lazy(() => import("@/pages/Sala"));
const Cucina = lazy(() => import("@/pages/Cucina"));
const QrPrint = lazy(() => import("@/pages/QrPrint"));
const Prenota = lazy(() => import("@/pages/Prenota"));
const TableMenu = lazy(() => import("@/pages/TableMenu"));
const PhotoGallery = lazy(() => import("@/pages/PhotoGallery"));
import { LockGuard } from "@/components/LockGuard";
import OnboardingTour from "@/components/OnboardingTour";
import Sidebar from "@/components/Sidebar";
import UndoButton from "@/components/UndoButton";
import SectionHelp from "@/components/SectionHelp";
import PageTour from "@/components/PageTour";
import GlobalSearch, { GSearchTrigger } from "@/components/GlobalSearch";
import MenuBacklinks from "@/components/MenuBacklinks";
import CountrySwitcher from "@/components/CountrySwitcher";
import { ConfirmHost } from "@/components/ConfirmHost";
import GeminiAssistant from "@/components/GeminiAssistant";
import { PATH_SECTION, ROLE_HOME } from "@/sections";

try {
  const _ref = new URLSearchParams(window.location.search).get("ref");
  if (_ref) localStorage.setItem("cc_ref", _ref.toUpperCase().slice(0, 20));
} catch {}

function AppShell() {
  const { user, loading, logout } = useAuth();
  const { t } = useI18n();
  const loc = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const [tbHidden, setTbHidden] = useState(sessionStorage.getItem("cc_tb") === "1");
  useEffect(() => {
    // correttore ortografico del browser su tutti i campi di testo (lingua = document.lang)
    const h = (e) => {
      const el = e.target;
      if (el && (el.tagName === "TEXTAREA" || (el.tagName === "INPUT" && (!el.type || ["text", "search", "email"].includes(el.type)))) && !el.hasAttribute("data-nospell")) {
        el.spellcheck = true;
      }
    };
    document.addEventListener("focusin", h);
    return () => document.removeEventListener("focusin", h);
  }, []);
  if (loading) return <div className="p-10 text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.allowed_sections) {
    const sec = PATH_SECTION[loc.pathname] || (loc.pathname.startsWith("/app/dept-shop/") ? "inventory" : undefined);
    const home = ROLE_HOME[user.access_role] || "/app/production";
    if ((!sec || !user.allowed_sections.includes(sec)) && loc.pathname !== home) return <Navigate to={home} replace />;
  }
  return (
    <div className="flex min-h-screen bg-background grain">
      <div className={`fixed inset-y-0 left-0 z-50 transition-transform duration-200 md:static md:z-auto md:translate-x-0 ${navOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <Sidebar onNavigate={() => setNavOpen(false)} />
      </div>
      {navOpen && <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={() => setNavOpen(false)} data-testid="mobile-nav-backdrop" />}
      <main className="flex-1 min-w-0">
        <div className="md:hidden flex items-center gap-3 px-4 py-3 border-b border-border bg-white/80 backdrop-blur sticky top-0 z-30 safe-top">
          <button onClick={() => setNavOpen(true)} className="p-1.5 rounded-lg hover:bg-muted" data-testid="btn-mobile-menu"><Menu className="w-5 h-5" /></button>
          <NavLink to="/app/azienda" className="serif font-semibold text-lg flex-1 flex items-center gap-2" data-testid="mobile-account-link">
            {user?.logo_path && <img src={photoUrl(user.logo_path)} alt="logo" className="w-7 h-7 rounded-lg object-cover" data-testid="mobile-business-logo" />}
            {user?.logo_path ? user?.name : "Chef Control"}
          </NavLink>
          {!user?.allowed_sections && <CountrySwitcher tid="country-switcher-btn-mobile" />}
          <GSearchTrigger mobile />
          <button onClick={() => window.location.reload()} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground" title="Ricarica" data-testid="btn-reload-mobile"><RotateCw className="w-4 h-4" /></button>
          <UndoButton />
          <button onClick={() => { logout(); }} className="text-sm text-muted-foreground flex items-center gap-1" data-testid="btn-mobile-logout"><LogOut className="w-4 h-4" /></button>
        </div>
        {user?.subscription_state?.status === "trial" && user.subscription_state.days_left <= 3 && !tbHidden && !user?.allowed_sections && (
          <div className="bg-[#7a1f1f] text-white text-sm px-4 py-2.5 flex items-center gap-3" data-testid="trial-banner">
            <AlarmClock className="w-4 h-4 shrink-0" />
            <span className="flex-1">{user.subscription_state.days_left <= 0 ? t("trialBannerToday") : t("trialBannerMsg").replace("{days}", user.subscription_state.days_left)}</span>
            <NavLink to="/app/billing" className="bg-white text-[#7a1f1f] font-bold text-xs px-4 py-1.5 rounded-full hover:bg-white/90 shrink-0" data-testid="trial-banner-cta">{t("trialBannerCta")}</NavLink>
            <button onClick={() => { sessionStorage.setItem("cc_tb", "1"); setTbHidden(true); }} className="p-1 opacity-70 hover:opacity-100" data-testid="trial-banner-close"><X className="w-4 h-4" /></button>
          </div>
        )}
        <Suspense fallback={<PageLoader />}><MenuBacklinks /><Outlet /></Suspense>
      </main>
      <GlobalSearch />
      <SectionHelp />
      <PageTour />
      <GeminiAssistant />
      <OnboardingTour />
    </div>
  );
}

function PageLoader() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center">
      <div className="w-8 h-8 rounded-full border-[3px] border-primary/20 border-t-primary animate-spin" />
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function Router() {
  const loc = useLocation();
  // OAuth callback detection — must run BEFORE routes
  if (loc.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Suspense fallback={<PageLoader />}>
    <ScrollToTop />
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Auth mode="login" />} />
      <Route path="/auth" element={<Auth mode="login" />} />
      <Route path="/register" element={<Auth mode="register" />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/chi-siamo" element={<ChiSiamo />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/portal/:code" element={<Portal />} />
      <Route path="/direzione/:code" element={<Direzione />} />
      <Route path="/payment/success" element={<PaymentResult mode="success" />} />
      <Route path="/payment/cancel" element={<PaymentResult mode="cancel" />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/developer" element={<Developer />} />
      <Route path="/clock/:code" element={<ClockPage />} />
      <Route path="/turni/:code" element={<MyShifts />} />
      <Route path="/doc/:kind/:id" element={<PublicDoc />} />
      <Route path="/firma/:token" element={<SignDelivery />} />
      <Route path="/trace/:kind/:id" element={<TraceView />} />
      <Route path="/sala" element={<Sala />} />
      <Route path="/sala/qr" element={<QrPrint />} />
      <Route path="/cucina" element={<Cucina />} />
      <Route path="/menu/:code" element={<TableMenu />} />
      <Route path="/prenota/:bid" element={<Prenota />} />
      <Route path="/app" element={<AppShell />}>
        <Route index element={<Dashboard />} />
        <Route path="cassa" element={<LockGuard section="cassa"><Cassa /></LockGuard>} />
        <Route path="prenotazioni" element={<Prenotazioni />} />
        <Route path="recipes" element={<LockGuard section="recipes"><Recipes /></LockGuard>} />
        <Route path="formula" element={<Formula />} />
        <Route path="inventory" element={<LockGuard section="inventory"><Inventory /></LockGuard>} />
        <Route path="inventario" element={<LockGuard section="inventory"><Inventario /></LockGuard>} />
        <Route path="storico-produzione" element={<LockGuard section="inventory"><ProductionHistory /></LockGuard>} />
        <Route path="devai" element={<DevAI />} />
        <Route path="devai/:mid" element={<DevAI />} />
        <Route path="lab" element={<LockGuard section="lab"><Lab /></LockGuard>} />
        <Route path="stats" element={<LockGuard section="stats"><Stats /></LockGuard>} />
        <Route path="azienda" element={<LockGuard section="azienda"><Azienda /></LockGuard>} />
        <Route path="guide" element={<Guide />} />
        <Route path="trash" element={<TrashPage />} />
        <Route path="ingredients-list" element={<LockGuard section="inglist"><IngredientsList /></LockGuard>} />
        <Route path="catalog" element={<LockGuard section="catalog"><Catalog /></LockGuard>} />
        <Route path="listino" element={<LockGuard section="catalog"><Listino /></LockGuard>} />
        <Route path="dept-shop/:dept" element={<DeptShop />} />
        <Route path="dept-overview" element={<DeptOverview />} />
        <Route path="foodcost" element={<FoodCost />} />
        <Route path="eventi" element={<SpecialDays />} />
        <Route path="foto" element={<PhotoGallery />} />
        <Route path="acquistati" element={<PurchasedProducts />} />
        <Route path="reorder" element={<LockGuard section="reorder"><Reorder /></LockGuard>} />
        <Route path="staff" element={<LockGuard section="staff"><Staff /></LockGuard>} />
        <Route path="shifts" element={<LockGuard section="staff"><Shifts /></LockGuard>} />
        <Route path="attendance" element={<LockGuard section="staff"><Attendance /></LockGuard>} />
        <Route path="documents" element={<LockGuard section="documents"><Documents /></LockGuard>} />
        <Route path="past-docs" element={<LockGuard section="documents"><PastDocs /></LockGuard>} />
        <Route path="labels-history" element={<LockGuard section="inventory"><LabelsHistory /></LockGuard>} />
        <Route path="payments" element={<LockGuard section="payments"><Payments /></LockGuard>} />
        <Route path="waste" element={<LockGuard section="waste"><Waste /></LockGuard>} />
        <Route path="invoices" element={<LockGuard section="invoices"><Invoices /></LockGuard>} />
        <Route path="orders" element={<LockGuard section="orders"><Orders /></LockGuard>} />
        <Route path="caldane" element={<LockGuard section="orders"><Caldane /></LockGuard>} />
        <Route path="ricorrenti" element={<LockGuard section="orders"><Ricorrenti /></LockGuard>} />
        <Route path="torte" element={<LockGuard section="orders"><Torte /></LockGuard>} />
        <Route path="avanzi" element={<LockGuard section="orders"><Avanzi /></LockGuard>} />
        <Route path="preventivi" element={<LockGuard section="orders"><Preventivi /></LockGuard>} />
        <Route path="b2b" element={<LockGuard section="orders"><B2B /></LockGuard>} />
        <Route path="messages" element={<Messages />} />
        <Route path="production" element={<LockGuard section="orders"><Production /></LockGuard>} />
        <Route path="partners" element={<LockGuard section="partners"><Partners kind="customer" /></LockGuard>} />
        <Route path="suppliers" element={<LockGuard section="partners"><Partners kind="supplier" /></LockGuard>} />
        <Route path="reports" element={<LockGuard section="reports"><Reports /></LockGuard>} />
        <Route path="billing" element={<LockGuard section="billing"><Billing /></LockGuard>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function NumberInputFlow() {
  useEffect(() => {
    const isEuro = (el) => el?.tagName === "INPUT" && el.getAttribute("data-euro") !== null;
    const isNum = (el) => el?.tagName === "INPUT" && (el.type === "number" || isEuro(el) || el.hasAttribute("data-num")) && !el.disabled && !el.readOnly;
    const setVal = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set;
    const fmt = (digits) => { const d = digits.replace(/^0+(?=\d)/, "").slice(0, 9); return d ? (parseInt(d, 10) / 100).toFixed(2) : ""; };
    const fresh = new WeakSet();
    const onInput = (e) => {
      const el = e.target;
      if (!isEuro(el) || !e.isTrusted) return;
      fresh.delete(el);
      const next = fmt((el.value || "").replace(/\D/g, ""));
      if (el.value !== next) setVal.call(el, next);
    };
    const onImgErr = (e) => {
      const el = e.target;
      if (!el || el.tagName !== "IMG" || !(el.src || "").includes("/api/photos/")) return;
      const tries = +(el.dataset.retry || 0);
      if (tries >= 2) return;
      el.dataset.retry = String(tries + 1);
      setTimeout(() => { if (el.isConnected) el.src = el.src.split("?")[0] + "?r=" + Date.now(); }, 1000 * (tries + 1));
    };
    const onImgLoad = (e) => {
      const el = e.target;
      if (el && el.tagName === "IMG" && el.dataset.retry && el.style.display === "none") el.style.display = "";
    };
    document.addEventListener("error", onImgErr, true);
    document.addEventListener("load", onImgLoad, true);
    document.addEventListener("input", onInput, true);
    // Prezzi stile cassa gestiti sul tasto: su type=number Firefox/Safari restituiscono "" per i valori
    // intermedi e spostano il cursore dopo la riscrittura -> il campo si azzerava mentre si digitava.
    const onEuroKey = (e) => {
      const el = e.target;
      if (!isEuro(el) || el.disabled || el.readOnly || e.ctrlKey || e.metaKey || e.altKey) return;
      const isDigit = /^[0-9]$/.test(e.key);
      if (!isDigit && e.key !== "Backspace" && e.key !== "Delete" && e.key !== "," && e.key !== ".") return;
      e.preventDefault();
      if (e.key === "," || e.key === ".") return;
      const n = parseFloat(el.value);
      const cur = fresh.has(el) || !(n > 0) ? "" : String(Math.round(n * 100));
      const next = isDigit ? fmt(cur + e.key) : fmt(cur.slice(0, -1));
      fresh.delete(el);
      setVal.call(el, next);
      el.dispatchEvent(new Event("input", { bubbles: true }));
    };
    document.addEventListener("keydown", onEuroKey, true);
    const sel = (el) => { try { el.select(); } catch {} if (isEuro(el)) fresh.add(el); };
    // Seleziona tutto solo al primo ingresso nel campo: un clic in un campo già attivo serve a
    // spostare il cursore per correggere una cifra, non a sostituire l'intero numero.
    let wasActive = null;
    const onDown = (e) => { wasActive = document.activeElement; };
    const onFocus = (e) => { if (isNum(e.target)) sel(e.target); };
    const onClick = (e) => { if (isNum(e.target) && wasActive !== e.target) sel(e.target); wasActive = null; };
    // La rotella su un type=number attivo cambiava il valore mentre si scorreva la pagina.
    const onWheel = (e) => { const el = e.target; if (el?.tagName === "INPUT" && el.type === "number" && el === document.activeElement) el.blur(); };
    const onKey = (e) => {
      if ((e.key !== "Enter" && e.key !== "Tab") || e.shiftKey || !isNum(e.target)) return;
      const list = Array.from(document.querySelectorAll('input[type="number"], input[data-euro], input[data-num]')).filter((el) => !el.disabled && !el.readOnly && el.offsetParent !== null);
      const i = list.indexOf(e.target);
      if (i > -1 && list[i + 1]) { e.preventDefault(); list[i + 1].focus(); sel(list[i + 1]); }
    };
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("focusin", onFocus);
    document.addEventListener("click", onClick);
    document.addEventListener("wheel", onWheel, { capture: true, passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("error", onImgErr, true);
      document.removeEventListener("load", onImgLoad, true);
      document.removeEventListener("input", onInput, true);
      document.removeEventListener("keydown", onEuroKey, true);
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("focusin", onFocus);
      document.removeEventListener("click", onClick);
      document.removeEventListener("wheel", onWheel, { capture: true });
      document.removeEventListener("keydown", onKey);
    };
  }, []);
  return null;
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <ConfirmHost />
          <NumberInputFlow />
          <Router />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}
